'use client'

import ReceiptPreviewModal from '@/components/billing/receipt-preview-modal'
import ConfirmModal from '@/components/confirm-modal'
import ApprovedHistorySection, {
    countApprovedHistory,
} from './approved-history-section'
import ApprovalTabs, { ApprovalTabKey } from './components/approval-tabs'
import ActionRequestCard from './components/action-request-card'
import PayableCard from './components/payable-card'
import RefundCard from './components/refund-card'
import QueueSection from './components/queue-section'
import PayoutBatchItemsModal from './components/payout-batch-items-modal'
import { findStatementAttachment, formatCurrency } from './format'
import {
    ApprovePayableMode,
    BillingApprovalItem,
    BulkResult,
    bulkApproveBillings,
    bulkRejectBillings,
    clearBillingExternalTransfer,
    getPendingBillingApprovals,
    markBillingExternallyTransferred,
    removeBillingApproval,
} from '@/lib/api/financial/billing-approval-api'
import {
    BillingActionRequestItem,
    RejectReasonCategory,
    bulkApproveBillingActionRequests,
    bulkRejectBillingActionRequests,
    deleteBillingActionRequest,
    getPendingBillingActionRequests,
} from '@/lib/api/financial/billing-action-request-api'
import {
    RefundMode,
    RefundRequestItem,
    bulkApproveRefunds,
    bulkRejectRefunds,
    clearRefundExternalRefund,
    getPendingRefundRequests,
    markRefundExternallyRefunded,
} from '@/lib/api/financial/billing-refund-api'
import {
    PayoutBatchAwaitingSubmission,
    getPayoutBatchesAwaitingSubmission,
} from '@/lib/api/financial/payout-batch-api'
import { isOfflinePaymentMethod } from '@/lib/api/common/interfaces/billing.interface'
import { useIsFinanceOperator } from '@/lib/hooks/use-is-finance-operator'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    MdCheckCircle,
    MdClose,
    MdHistory,
    MdInfoOutline,
    MdReceipt,
    MdReplay,
    MdRefresh,
    MdSearch,
    MdTask,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import PasswordField from '../payment-account/components/password-field'

/**
 * Como a conta a pagar será quitada ao aprovar. Nem toda conta sai pela conta
 * digital: às vezes o financeiro já pagou por outro banco, e aprovar não pode
 * mandar o dinheiro de novo.
 *
 * **Aprovar é o último clique: sempre dá baixa.** O modo `APPROVE_ONLY` ("apenas
 * aprovar", que mudava só o `approvalStatus` sem tocar no dinheiro) foi retirado da
 * tela — ele deixava o lançamento aprovado, pendente e fora da fila, dependendo de
 * alguém lembrar de voltar para dar a baixa. Quando pagar depende de data, o campo
 * que diz isso é o **vencimento**: lança-se para a data certa e aprova-se nela.
 *
 * O backend continua aceitando `APPROVE_ONLY` (chamadas antigas, API), mas a UI não
 * oferece mais.
 */
const APPROVE_MODES: {
    value: ApprovePayableMode
    label: string
    description: string
}[] = [
    {
        value: 'TRANSFER',
        label: 'Aprovar e transferir',
        description:
            'Executa a transferência pela conta digital (entre contas da plataforma quando o destinatário tem conta aqui, sem tarifa; PIX quando tem chave; TED quando só há dados bancários).',
    },
    {
        value: 'ALREADY_TRANSFERRED',
        label: 'Já foi transferida (pagamento externo)',
        description:
            'O financeiro já pagou por outra instituição. A conta é marcada como paga e conciliada, sem nova transferência.',
    },
]

const APPROVE_MODE_ACTION: Record<ApprovePayableMode, string> = {
    TRANSFER: 'Aprovar e transferir',
    APPROVE_ONLY: 'Apenas aprovar',
    ALREADY_TRANSFERRED: 'Aprovar como paga',
}

/**
 * Por onde o dinheiro volta ao cliente. Espelha os modos da conta a pagar: o
 * estorno automático só existe quando o pagamento ENTROU pela conta digital.
 */
const REFUND_MODES: {
    value: RefundMode
    label: string
    description: string
}[] = [
    {
        value: 'BAAS',
        label: 'Estornar pela conta digital',
        description:
            'Devolve o valor ao cliente pelo provedor, na cobrança original. Disponível apenas para pagamentos recebidos pela conta digital.',
    },
    {
        value: 'ALREADY_REFUNDED',
        label: 'Já foi estornado (devolução externa)',
        description:
            'O dinheiro já voltou ao cliente por fora (espécie, outro banco). Registra o estorno e a instituição, sem movimentar a conta digital.',
    },
]

const REFUND_MODE_ACTION: Record<RefundMode, string> = {
    BAAS: 'Aprovar e estornar',
    ALREADY_REFUNDED: 'Registrar estorno',
}

/**
 * Um alvo de confirmação é sempre uma lista de ids. Uma linha só é o caso de `ids`
 * com um elemento — assim o fluxo individual e o em massa compartilham o mesmo
 * caminho de execução e os mesmos modais.
 */
const APPROVAL_TABS: ApprovalTabKey[] = ['payables', 'actions', 'refunds', 'approved']

/**
 * Item que a tela deve destacar ao abrir, vindo da URL (`?tab=&search=&focus=`).
 * É como a cobrança leva o operador à solicitação que a está segurando: a aba
 * certa, filtrada pelo número do lançamento e com o item já marcado.
 */
type FocusTarget = { tab: ApprovalTabKey; id: number }

type ConfirmTarget = {
    kind: 'payable' | 'action' | 'refund'
    ids: number[]
    action: 'approve' | 'reject'
}

export default function BillingApprovalsPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const initialTab = searchParams.get('tab') as ApprovalTabKey | null
    const [tab, setTab] = useState<ApprovalTabKey>(
        initialTab && APPROVAL_TABS.includes(initialTab) ? initialTab : 'payables'
    )
    const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(() => {
        const id = Number(searchParams.get('focus'))
        return Number.isInteger(id) && id > 0 && initialTab && APPROVAL_TABS.includes(initialTab)
            ? { tab: initialTab, id }
            : null
    })
    /** Item destacado na lista — some sozinho depois de alguns segundos. */
    const [highlightId, setHighlightId] = useState<number | null>(null)
    const [payables, setPayables] = useState<BillingApprovalItem[]>([])
    const [actionRequests, setActionRequests] = useState<
        BillingActionRequestItem[]
    >([])
    const [refunds, setRefunds] = useState<RefundRequestItem[]>([])
    const [approvedCount, setApprovedCount] = useState(0)
    const [loading, setLoading] = useState(true)
    /**
     * Solicitações agrupadas criadas e nunca enviadas para aprovação.
     *
     * Não estão nesta fila (nascem fora dela) e nem por isso são inofensivas: cada
     * uma prende os repasses que soma, que somem do agrupamento até alguém enviá-la
     * ou excluí-la. Sem este aviso, ninguém descobria — em produção duas ficaram
     * paradas dias, uma com 17 atendimentos.
     */
    const [awaitingSubmission, setAwaitingSubmission] = useState<{
        items: PayoutBatchAwaitingSubmission[]
        totalAmount: number
    }>({ items: [], totalAmount: 0 })

    /**
     * Busca das filas. `searchInput` é o que está digitado; `search` é o termo já
     * estabilizado (debounce) que vai ao servidor.
     *
     * **Roda no servidor, não no navegador.** A fila de contas a pagar é feita de
     * solicitações AGRUPADAS: o parceiro está no lote, mas o paciente e o número do
     * repasse estão nos itens dele — filtrar a lista carregada só acharia o que já
     * estava visível na tela.
     */
    const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '')
    const [search, setSearch] = useState(() => (searchParams.get('search') ?? '').trim())

    /** Página de cada fila, separada: trocar de aba não perde o lugar da outra. */
    const [payablesPage, setPayablesPage] = useState(1)
    const [actionsPage, setActionsPage] = useState(1)
    const [refundsPage, setRefundsPage] = useState(1)

    const [confirming, setConfirming] = useState<ConfirmTarget | null>(null)
    const [reason, setReason] = useState('')
    const [reasonCategory, setReasonCategory] =
        useState<RejectReasonCategory | null>(null)
    /** Comprovante aberto no visualizador (o aprovador confere antes de decidir). */
    const [previewReceipt, setPreviewReceipt] = useState<{
        url: string
        name: string
    } | null>(null)
    const [password, setPassword] = useState('')
    /** Só vale para conta a pagar: define se a aprovação movimenta a conta digital. */
    const [approveMode, setApproveMode] =
        useState<ApprovePayableMode>('TRANSFER')
    const [transferredVia, setTransferredVia] = useState('')

    const [selectedPayables, setSelectedPayables] = useState<number[]>([])
    const [selectedActions, setSelectedActions] = useState<number[]>([])
    const [selectedRefunds, setSelectedRefunds] = useState<number[]>([])
    const [refundMode, setRefundMode] = useState<RefundMode>('BAAS')
    const [refundedVia, setRefundedVia] = useState('')

    /** Conta que está sendo marcada como "já paga por fora" (pede a instituição). */
    const [externalTarget, setExternalTarget] =
        useState<BillingApprovalItem | null>(null)
    const [externalVia, setExternalVia] = useState('')
    const [markingExternal, setMarkingExternal] = useState<number | null>(null)

    /** Estorno já feito por fora (outro banco): espelha o "transferido" das contas a pagar. */
    const [refundExternalTarget, setRefundExternalTarget] =
        useState<RefundRequestItem | null>(null)
    const [refundExternalVia, setRefundExternalVia] = useState('')
    const [markingRefundExternal, setMarkingRefundExternal] = useState<
        number | null
    >(null)

    const [processing, setProcessing] = useState(false)

    /** Solicitação que está sendo excluída da fila (confirmação em modal). */
    const [deleting, setDeleting] = useState<BillingActionRequestItem | null>(
        null
    )
    const [deletingBusy, setDeletingBusy] = useState(false)
    /** Conta a pagar que o operador quer tirar da fila (pedido aberto por engano). */
    const [deletingPayable, setDeletingPayable] =
        useState<BillingApprovalItem | null>(null)
    /**
     * Estorno a ser tirado da fila. O pedido de estorno **é** um
     * `BillingActionRequest` (action `REFUND`), então quem exclui é a mesma rota das
     * solicitações de ação — não há endpoint próprio a criar.
     */
    const [deletingRefund, setDeletingRefund] =
        useState<RefundRequestItem | null>(null)

    /** Solicitação agrupada aberta no "Ver grupo" (a composição do lote). */
    const [batchTarget, setBatchTarget] = useState<BillingApprovalItem | null>(
        null
    )

    const { user } = useAuth()
    const {
        isOperator,
        hasOperators,
        loading: loadingOperator,
    } = useIsFinanceOperator()
    const { canUpdate, canDelete } = useResourcePermissions(
        'financial',
        'billing_approvals'
    )

    /**
     * Ações sensíveis (marcar como pago / cancelar) só tocam o lançamento, nada sai
     * da conta digital — então quem decide é a role, não a eleição de operador
     * financeiro. Quem confere o comprovante é o financeiro, que raramente é o dono
     * da empresa e não pode depender de um operador eleito estar por perto.
     * O backend aplica a mesma regra — aqui é só a UI.
     */
    const canApproveActions = canUpdate

    /**
     * Excluir tira o pedido da fila sem decidir. **Uma regra para as três filas** —
     * solicitação de ação (aba "Aprovações"), conta a pagar e estorno: `delete` na role,
     * e nada além disso.
     *
     * A conta a pagar chegou a exigir também ser o solicitante ou operador, e a regra
     * caiu no mesmo dia: o dono da empresa (`UserType.COMPANY`) tem acesso total mas não
     * é solicitante nem operador eleito — ficava sem conseguir desfazer um pedido
     * errado. Excluir não decide nada nem move dinheiro; **recusar**, que é decisão,
     * continua restrito a operador.
     */
    const canDeleteQueueItem = canDelete

    /**
     * Quem abre a aprovação do estorno. O que decide é **o modo**, não como o dinheiro
     * entrou (mesma regra do `assertRefundApprover` no backend):
     *
     * - **"Já foi estornado"** (registra devolução feita por fora): `delete` na fila
     *   basta. Nada sai da conta digital — é lançamento, igual ao "já foi transferida"
     *   da conta a pagar.
     * - **"Estornar pela conta digital"**: só operador financeiro eleito, porque aí sai
     *   saldo do provedor. A opção é escondida de quem não é operador (ver o modal).
     *
     * Então quem tem `delete` **ou** é operador chega ao modal; qual dos dois modos ele
     * pode usar é decidido lá dentro.
     */
    const canApproveRefund = (_item: RefundRequestItem): boolean =>
        isOperator || canDelete

    /**
     * Contas a pagar e estornos disparam movimentação real no provedor: seguem
     * restritos a operador financeiro eleito, e ainda exigem senha.
     */
    const canApprovePayables = isOperator

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search])

    // Buscar só depois que o operador para de digitar: cada tecla seria uma consulta.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 400)
        return () => clearTimeout(timer)
    }, [searchInput])

    /**
     * Filtrar troca as linhas debaixo da seleção: o que estava marcado sai da lista
     * mas continuaria no lote a aprovar — e o operador aprovaria contas que não vê.
     */
    useEffect(() => {
        clearSelection()
        setPayablesPage(1)
        setActionsPage(1)
        setRefundsPage(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search])

    /**
     * Cobrança paga por fora não tem o que estornar no provedor — o modo BaaS nem
     * aparece na lista. Sem este ajuste, `refundMode` continuaria em 'BAAS' (o
     * padrão) com nenhum rádio marcado na tela, e o operador aprovaria um estorno
     * pelo provedor que o backend recusaria.
     */
    useEffect(() => {
        if (confirming?.kind !== 'refund' || confirming.action !== 'approve') {
            return
        }
        setRefundMode(
            refundSelectionCanUseBaas(confirming.ids) ? 'BAAS' : 'ALREADY_REFUNDED'
        )
    }, [confirming])

    /**
     * Aba e busca ficam na URL: voltar para esta tela (ou recarregar) devolve o
     * operador ao mesmo lugar, e o link vindo da cobrança funciona igual.
     */
    const changeTab = (next: ApprovalTabKey) => {
        setTab(next)
        setSearchParams(
            prev => {
                const params = new URLSearchParams(prev)
                params.set('tab', next)
                params.delete('focus')
                return params
            },
            { replace: true }
        )
    }

    useEffect(() => {
        setSearchParams(
            prev => {
                const params = new URLSearchParams(prev)
                if (search) params.set('search', search)
                else params.delete('search')
                return params
            },
            { replace: true }
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search])

    /**
     * Aplica o `focus` da URL depois que as filas carregam: marca o item (quando
     * quem está vendo pode decidir aquela fila) e rola até ele. Uma vez só — o
     * parâmetro sai da URL para não reaplicar a cada recarga das filas.
     */
    useEffect(() => {
        // Espera também a eleição de operador: antes dela, `canApprovePayables`
        // ainda é falso e o item não seria marcado para quem pode aprová-lo.
        if (loading || loadingOperator || !focusTarget) return
        const { tab: focusTab, id } = focusTarget

        const present =
            (focusTab === 'payables' && payables.some(item => item.id === id))
            || (focusTab === 'actions' && actionRequests.some(item => item.id === id))
            || (focusTab === 'refunds' && refunds.some(item => item.id === id))

        if (present) {
            if (focusTab === 'payables' && canApprovePayables) setSelectedPayables([id])
            if (focusTab === 'actions' && canApproveActions) setSelectedActions([id])
            if (focusTab === 'refunds' && canApprovePayables) setSelectedRefunds([id])
            setHighlightId(id)
            requestAnimationFrame(() => {
                document
                    .getElementById(`approval-item-${focusTab}-${id}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            })
        }

        setFocusTarget(null)
        setSearchParams(
            prev => {
                const params = new URLSearchParams(prev)
                params.delete('focus')
                return params
            },
            { replace: true }
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, loadingOperator, focusTarget, payables, actionRequests, refunds])

    useEffect(() => {
        if (highlightId == null) return
        const timer = setTimeout(() => setHighlightId(null), 4000)
        return () => clearTimeout(timer)
    }, [highlightId])

    /** Moldura do item que veio do link da cobrança. */
    const focusFrame = (focusTab: ApprovalTabKey, id: number) => ({
        id: `approval-item-${focusTab}-${id}`,
        className:
            highlightId === id && tab === focusTab
                ? 'rounded-xl ring-2 ring-secondary ring-offset-2 ring-offset-base-100 transition-shadow'
                : 'rounded-xl transition-shadow',
    })

    const load = async () => {
        setLoading(true)
        try {
            const term = search || undefined
            const [pay, req, ref, approved, awaiting] = await Promise.all([
                getPendingBillingApprovals(term),
                getPendingBillingActionRequests(term),
                getPendingRefundRequests(term),
                // O badge de "Aprovados" conta o histórico INTEIRO: com o termo, ele
                // passaria a contar o recorte e a aba anunciaria um número que não é
                // o do histórico. Quem recorta lá dentro é a própria seção.
                countApprovedHistory(),
                getPayoutBatchesAwaitingSubmission(),
            ])
            if (pay.success && pay.result) setPayables(pay.result)
            if (req.success && req.result) setActionRequests(req.result)
            if (ref.success && ref.result) setRefunds(ref.result)
            setApprovedCount(approved)
            setAwaitingSubmission(
                awaiting.success && awaiting.result
                    ? awaiting.result
                    : { items: [], totalAmount: 0 }
            )
        } finally {
            setLoading(false)
        }
    }

    const toggleOne = (
        id: number,
        selected: number[],
        setSelected: (ids: number[]) => void
    ) =>
        setSelected(
            selected.includes(id)
                ? selected.filter(x => x !== id)
                : [...selected, id]
        )

    const toggleAll = (
        allIds: number[],
        selected: number[],
        setSelected: (ids: number[]) => void
    ) => setSelected(selected.length === allIds.length ? [] : allIds)

    /**
     * Contas que o "Selecionar todas" pode marcar: só as **vencidas ou vencendo
     * hoje**.
     *
     * O aprovador recebe dezenas de linhas e marca tudo de uma vez — é o fluxo
     * normal, e é rápido justamente porque ele não lê data por data. Com o financeiro
     * gerando lotes escalonados (parte para hoje, parte para 30 dias, parte para 60),
     * um clique em "todas" mandaria dinheiro embora com um mês de antecedência. Não dá
     * para desfazer transferência.
     *
     * Conta futura não some da fila nem fica bloqueada: ela só exige o clique
     * individual, que é a diferença entre pagar adiantado por engano e pagar
     * adiantado de propósito.
     */
    const isDueForPayment = (dueDate?: string | null): boolean => {
        if (!dueDate) return true // sem vencimento definido: não há o que antecipar
        const today = new Date()
        today.setHours(23, 59, 59, 999)
        return new Date(dueDate) <= today
    }

    const payablesDueNow = payables.filter(b => isDueForPayment(b.dueDate))
    const payablesUpcoming = payables.length - payablesDueNow.length

    const clearSelection = () => {
        setSelectedPayables([])
        setSelectedActions([])
        setSelectedRefunds([])
    }

    /**
     * Declara que o dinheiro já saiu por outro banco. Não movimenta nada aqui: encerra
     * a fila (aprovada, paga e conciliada) porque o pagamento já aconteceu lá fora.
     */
    const handleMarkExternal = async () => {
        if (!externalTarget || !externalVia.trim()) return

        setMarkingExternal(externalTarget.id)
        try {
            const res = await markBillingExternallyTransferred(
                externalTarget.id,
                externalVia.trim()
            )
            if (res.success) {
                toast.success(
                    `Conta dada como paga via ${externalVia.trim()} e retirada da fila. Nenhuma transferência foi feita.`
                )
                setExternalTarget(null)
                setExternalVia('')
                load()
            } else {
                toast.error(res.error || 'Erro ao marcar a conta')
            }
        } catch {
            toast.error('Erro de comunicação com o servidor')
        } finally {
            setMarkingExternal(null)
        }
    }

    const handleClearExternal = async (billingId: number) => {
        setMarkingExternal(billingId)
        try {
            const res = await clearBillingExternalTransfer(billingId)
            if (res.success) {
                toast.success('Marcação removida')
                load()
            } else {
                toast.error(res.error || 'Erro ao remover a marcação')
            }
        } catch {
            toast.error('Erro de comunicação com o servidor')
        } finally {
            setMarkingExternal(null)
        }
    }

    const handleMarkRefundExternal = async () => {
        if (!refundExternalTarget || !refundExternalVia.trim()) return
        setMarkingRefundExternal(refundExternalTarget.id)
        try {
            const res = await markRefundExternallyRefunded(
                refundExternalTarget.id,
                refundExternalVia.trim()
            )
            if (res.success) {
                toast.success(
                    `Estorno marcado como já feito via ${refundExternalVia.trim()}. A aprovação não vai estornar de novo.`
                )
                setRefundExternalTarget(null)
                setRefundExternalVia('')
                load()
            } else {
                toast.error(res.error || 'Erro ao marcar o estorno')
            }
        } catch {
            toast.error('Erro de comunicação com o servidor')
        } finally {
            setMarkingRefundExternal(null)
        }
    }

    const handleClearRefundExternal = async (requestId: number) => {
        setMarkingRefundExternal(requestId)
        try {
            const res = await clearRefundExternalRefund(requestId)
            if (res.success) {
                toast.success('Marcação removida')
                load()
            } else {
                toast.error(res.error || 'Erro ao remover a marcação')
            }
        } catch {
            toast.error('Erro de comunicação com o servidor')
        } finally {
            setMarkingRefundExternal(null)
        }
    }

    /** Uma requisição por lote — o backend itera e devolve o que falhou. */
    const callBulk = (target: ConfirmTarget) => {
        const motivo = reason || 'Sem motivo informado'
        if (target.kind === 'payable') {
            return target.action === 'approve'
                ? bulkApproveBillings(target.ids, password, {
                      mode: approveMode,
                      transferredVia:
                          approveMode === 'ALREADY_TRANSFERRED'
                              ? transferredVia.trim()
                              : undefined,
                  })
                : bulkRejectBillings(target.ids, motivo)
        }
        if (target.kind === 'refund') {
            // Sem `amount`: cada solicitação devolve o valor que pediu. Um valor único
            // aplicado ao lote seria o valor errado em todas as cobranças menos uma.
            return target.action === 'approve'
                ? bulkApproveRefunds(target.ids, password, {
                      mode: refundMode,
                      refundedVia:
                          refundMode === 'ALREADY_REFUNDED'
                              ? refundedVia.trim()
                              : undefined,
                  })
                : bulkRejectRefunds(target.ids, motivo)
        }
        return target.action === 'approve'
            ? bulkApproveBillingActionRequests(target.ids)
            : bulkRejectBillingActionRequests(
                  target.ids,
                  motivo,
                  reasonCategory || 'OTHER'
              )
    }

    /**
     * Exclui a solicitação sem decidir a ação. A lista é recarregada porque a
     * cobrança sai de PENDING_APPROVAL e volta a aceitar novo pedido.
     */
    const handleDelete = async () => {
        if (!deleting) return
        setDeletingBusy(true)
        try {
            const res = await deleteBillingActionRequest(deleting.id)
            if (!res.success) {
                return toast.error(res.error || 'Erro ao excluir solicitação')
            }
            toast.success('Solicitação excluída da fila')
            setSelectedActions(ids => ids.filter(id => id !== deleting.id))
            setDeleting(null)
            await load()
        } finally {
            setDeletingBusy(false)
        }
    }

    /**
     * Tira a conta a pagar da fila. No lote, o backend solta os repasses e apaga a
     * conta agrupada — os repasses voltam ao relatório de Lançamentos e o financeiro
     * refaz o recorte.
     */
    const handleDeletePayable = async () => {
        if (!deletingPayable) return
        setDeletingBusy(true)
        try {
            const res = await removeBillingApproval(deletingPayable.id)
            if (!res.success) {
                return toast.error(res.error || 'Erro ao excluir solicitação')
            }
            toast.success(
                res.result?.wasPayoutBatch
                    ? 'Solicitação excluída. Os repasses voltaram para o agrupamento.'
                    : 'Conta a pagar retirada da fila.'
            )
            setSelectedPayables(ids =>
                ids.filter(id => id !== deletingPayable.id)
            )
            setDeletingPayable(null)
            await load()
        } finally {
            setDeletingBusy(false)
        }
    }

    /**
     * "Já foi estornado por outro banco" — o dinheiro voltou por fora.
     *
     * Quem **pode aprovar** vai direto para a aprovação (modo "Já foi estornado", que o
     * efeito acima já pré-seleciona): um clique conclui, a cobrança fica REFUNDED ou
     * PARTIALLY_REFUNDED conforme o valor, e a senha é pedida no caminho.
     *
     * ⚠️ Antes este botão apenas MARCAVA o pedido, e era só isso — a cobrança seguia
     * PAID. Em produção quatro pedidos ficaram parados assim, com o dinheiro já
     * devolvido ao cliente. Mas o botão **não pode estornar por conta própria**: a rota
     * de marcação exige apenas `read` e não pede senha, então efetivar ali deixaria quem
     * só enxerga a fila estornar cobranças — disparando revogação de repasse ao
     * parceiro, pontos e contabilidade. Levar à aprovação resolve o meio-caminho sem
     * abrir esse buraco.
     *
     * Quem **não pode aprovar** continua só marcando: é o que ele consegue fazer, e a
     * marcação evita que a aprovação de outra pessoa estorne de novo pela conta digital.
     */
    const handleAlreadyRefunded = (item: RefundRequestItem) => {
        if (canApproveRefund(item)) {
            setConfirming({ kind: 'refund', ids: [item.id], action: 'approve' })
            setRefundedVia(item.externalRefund?.via ?? '')
            return
        }
        setRefundExternalTarget(item)
    }

    /** Tira o pedido de estorno da fila; a cobrança volta a aceitar um novo. */
    const handleDeleteRefund = async () => {
        if (!deletingRefund) return
        setDeletingBusy(true)
        try {
            const res = await deleteBillingActionRequest(deletingRefund.id)
            if (!res.success) {
                return toast.error(res.error || 'Erro ao excluir solicitação')
            }
            toast.success('Solicitação de estorno excluída da fila')
            setSelectedRefunds(ids =>
                ids.filter(id => id !== deletingRefund.id)
            )
            setDeletingRefund(null)
            await load()
        } finally {
            setDeletingBusy(false)
        }
    }

    const handleConfirm = async () => {
        if (!confirming) return
        // Aprovar PAYABLE dispara transferência, e aprovar ESTORNO devolve dinheiro:
        // os dois movimentam saldo para fora e exigem a senha do operador.
        if (
            (confirming.kind === 'payable' || confirming.kind === 'refund') &&
            confirming.action === 'approve' &&
            !password
        ) {
            return toast.warning('Confirme sua senha para aprovar')
        }
        setProcessing(true)
        try {
            const res = await callBulk(confirming)
            if (!res.success || !res.result) {
                return toast.error(res.error || 'Erro ao processar')
            }

            // Um lote parcial volta com HTTP 200: o que interessa é `failed`, não
            // `res.success`. Os ids em `succeeded` já foram efetivados.
            const { succeeded, failed }: BulkResult = res.result
            const verb =
                confirming.action === 'approve' ? 'aprovada' : 'rejeitada'
            const plural = succeeded.length === 1 ? verb : `${verb}s`

            if (failed.length === 0) {
                toast.success(`${succeeded.length} ${plural}`)
            } else if (succeeded.length === 0) {
                toast.error(failed[0].error || 'Erro ao processar')
            } else {
                // Sucesso parcial: as que passaram JÁ foram executadas (no caso de
                // conta a pagar, o dinheiro saiu). O operador precisa saber disso.
                toast.warning(
                    `${succeeded.length} ${plural}, ${failed.length} com erro. As que falharam continuam pendentes.`
                )
            }

            if (succeeded.length > 0) {
                setConfirming(null)
                setReason('')
                setReasonCategory(null)
                setPassword('')
                setApproveMode('TRANSFER')
                setTransferredVia('')
                setRefundMode('BAAS')
                setRefundedVia('')
                clearSelection()
                load()
            }
        } catch {
            toast.error('Erro de comunicação com o servidor')
        } finally {
            setProcessing(false)
        }
    }

    const sumPayables = (ids: number[]) =>
        payables
            .filter(b => ids.includes(b.id))
            .reduce((sum, b) => sum + b.amount, 0)

    /** Quanto seria devolvido ao cliente nas solicitações de estorno selecionadas. */
    const sumRefunds = (ids: number[]) =>
        refunds
            .filter(r => ids.includes(r.id))
            .reduce(
                (sum, r) =>
                    sum +
                    (r.payload?.refundAmount ?? r.billing.refundableAmount),
                0
            )

    /**
     * Estorno pela conta digital só existe se a cobrança entrou por lá. Num lote
     * misto, oferecer o modo BaaS levaria o operador a aprovar algo que o backend
     * vai recusar item a item.
     */
    const refundSelectionCanUseBaas = (ids: number[]) =>
        ids.length > 0 &&
        refunds
            .filter(r => ids.includes(r.id))
            .every(r => r.billing.canRefundViaBaas)


    /** Quantas linhas o modal aberto vai processar (1 no fluxo individual). */
    const confirmingCount = confirming?.ids.length ?? 0


    const totalPending =
        payables.length + actionRequests.length + refunds.length

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdCheckCircle className="text-secondary" />
                        Aprovações Financeiras
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1 max-w-2xl">
                        {/* Com busca ativa, o número é o do recorte — dizer "aguardam
                            decisão" faria a fila parecer menor do que é. */}
                        {search
                            ? `${totalPending} ${totalPending === 1 ? 'solicitação encontrada' : 'solicitações encontradas'} nas filas pendentes.`
                            : totalPending > 0
                              ? `${totalPending} ${totalPending === 1 ? 'solicitação aguarda' : 'solicitações aguardam'} decisão.`
                              : 'Contas a pagar, estornos e ações sensíveis aguardando decisão.'}
                    </p>
                </div>
                <button
                    className="btn btn-sm btn-ghost gap-2"
                    onClick={load}
                    disabled={loading}
                >
                    <MdRefresh className={loading ? 'animate-spin' : ''} />
                    Atualizar
                </button>
            </div>

            {!loadingOperator && !isOperator && hasOperators && (
                <div className="alert alert-warning mb-4">
                    <MdInfoOutline className="text-xl" />
                    <span className="text-sm">
                        {canApproveActions
                            ? 'Contas a pagar e estornos só podem ser aprovados por operadores financeiros eleitos, porque movimentam dinheiro para fora da conta digital. As ações sensíveis você pode aprovar normalmente.'
                            : 'Contas a pagar e estornos são aprovados por operadores financeiros eleitos. Você pode visualizar a lista mas não executar ações.'}
                    </span>
                </div>
            )}

            {/* Solicitações agrupadas que ficaram no meio do caminho. Vem antes das
                abas de propósito: elas NÃO estão em nenhuma fila, e é justamente por
                isso que passavam despercebidas enquanto prendiam repasses. */}
            {awaitingSubmission.items.length > 0 && (
                <div className="alert alert-warning items-start mb-4 flex-col">
                    <div className="flex items-start gap-3 w-full">
                        <MdWarning className="text-xl shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <span className="font-semibold">
                                {awaitingSubmission.items.length === 1
                                    ? '1 solicitação agrupada aguarda envio'
                                    : `${awaitingSubmission.items.length} solicitações agrupadas aguardam envio`}{' '}
                                ({formatCurrency(awaitingSubmission.totalAmount)})
                            </span>
                            <p className="mt-1 text-base-content/80">
                                Foram geradas mas nunca enviadas para aprovação, então
                                não aparecem nas filas abaixo — e os repasses que elas
                                somam ficam bloqueados para novo agrupamento. Abra cada
                                uma e clique em <strong>Enviar para aprovação</strong>,
                                ou exclua a solicitação para liberar os lançamentos.
                            </p>
                        </div>
                    </div>
                    <ul className="w-full flex flex-col gap-1 mt-1 pl-8">
                        {awaitingSubmission.items.map(batch => (
                            <li
                                key={batch.id}
                                className="flex items-center justify-between gap-3 text-sm"
                            >
                                <a
                                    href={`/financial/billings/${batch.id}`}
                                    className="link link-hover font-medium truncate"
                                >
                                    #{batch.id} · {batch.recipientName}
                                </a>
                                <span className="shrink-0 text-base-content/70">
                                    {batch.itemCount}{' '}
                                    {batch.itemCount === 1
                                        ? 'atendimento'
                                        : 'atendimentos'}{' '}
                                    · {formatCurrency(batch.amount)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <label className="input input-bordered flex items-center gap-2 mb-4">
                <MdSearch className="text-base-content/50" />
                {/*
                 * O Chrome chegou a pintar um e-mail salvo nesta caixa ao abrir
                 * a página, e o primeiro clique confirmava o preenchimento: as
                 * três filas eram refeitas com esse termo e iam a zero, com o
                 * operador lendo "nenhuma conta aguardando aprovação" numa fila
                 * cheia. A causa não estava aqui — era o campo de senha do
                 * `step-up-modal`, que ficava no DOM de toda página e fazia o
                 * navegador tratar o sistema como tela de login (ver o
                 * comentário lá). Resolvido na raiz, esta caixa voltou a ser uma
                 * busca comum; `type="search"` e `autoComplete="off"` ficam por
                 * serem o que ela é, não como remendo.
                 */}
                <input
                    type="search"
                    autoComplete="off"
                    name="approval-queue-search"
                    className="grow [&::-webkit-search-cancel-button]:appearance-none"
                    placeholder="Buscar por parceiro, fornecedor, profissional, cliente, descrição ou nº do lançamento"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                />
                {searchInput && (
                    <button
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={() => setSearchInput('')}
                        aria-label="Limpar busca"
                    >
                        <MdClose />
                    </button>
                )}
            </label>

            {search && (
                <p className="text-xs text-base-content/60 -mt-2 mb-4">
                    Mostrando resultados para <strong>{search}</strong>. O número do
                    lançamento também encontra a solicitação agrupada que o contém.
                </p>
            )}

            <ApprovalTabs
                active={tab}
                onChange={changeTab}
                loading={loading}
                tabs={[
                    {
                        key: 'payables',
                        label: 'Contas a Pagar',
                        icon: MdReceipt,
                        count: payables.length,
                        highlight: true,
                    },
                    {
                        key: 'actions',
                        label: 'Aprovações',
                        icon: MdTask,
                        count: actionRequests.length,
                        highlight: true,
                    },
                    {
                        key: 'refunds',
                        label: 'Estornos',
                        icon: MdReplay,
                        count: refunds.length,
                        highlight: true,
                    },
                    {
                        key: 'approved',
                        label: 'Aprovados',
                        icon: MdHistory,
                        count: approvedCount,
                    },
                ]}
            />

            <div className="mt-5">
                {tab === 'payables' && (
                    <QueueSection
                        items={payables}
                        loading={loading}
                        page={payablesPage}
                        onPageChange={setPayablesPage}
                        label="conta"
                        emptyTitle={
                            search
                                ? 'Nenhuma conta a pagar encontrada para esta busca.'
                                : 'Nenhuma conta a pagar aguardando aprovação.'
                        }
                        emptyHint={
                            search
                                ? 'A busca cobre parceiro, fornecedor, profissional, cliente, descrição e o número do lançamento — inclusive dos repasses dentro de uma solicitação agrupada.'
                                : 'Repasses a parceiros e profissionais entram aqui quando agrupados numa solicitação.'
                        }
                        selection={
                            canApprovePayables
                                ? {
                                      selectedCount: selectedPayables.length,
                                      // O "todas" conta só as vencidas: é o que ele
                                      // marca de uma vez. As a vencer continuam na
                                      // lista, clicáveis uma a uma.
                                      totalSelectable: payablesDueNow.length,
                                      upcomingCount: payablesUpcoming,
                                      amountText: `Total ${formatCurrency(sumPayables(selectedPayables))}`,
                                      onToggleAll: () =>
                                          toggleAll(
                                              payablesDueNow.map(b => b.id),
                                              selectedPayables,
                                              setSelectedPayables
                                          ),
                                      onApprove: () =>
                                          setConfirming({
                                              kind: 'payable',
                                              ids: selectedPayables,
                                              action: 'approve',
                                          }),
                                      onReject: () =>
                                          setConfirming({
                                              kind: 'payable',
                                              ids: selectedPayables,
                                              action: 'reject',
                                          }),
                                      onClear: () => setSelectedPayables([]),
                                  }
                                : undefined
                        }
                        renderItem={item => (
                            <div {...focusFrame('payables', item.id)}>
                            <PayableCard
                                item={item}
                                selectable={canApprovePayables}
                                selected={selectedPayables.includes(item.id)}
                                onToggleSelect={() =>
                                    toggleOne(
                                        item.id,
                                        selectedPayables,
                                        setSelectedPayables
                                    )
                                }
                                canApprove={canApprovePayables}
                                onDelete={
                                    canDeleteQueueItem
                                        ? () => setDeletingPayable(item)
                                        : undefined
                                }
                                onApprove={() => {
                                    // Conta já marcada como paga por fora: o modal
                                    // abre no modo correspondente. Abrindo em
                                    // "Aprovar e transferir", quem clicasse direto
                                    // mandaria uma transferência de um valor que já
                                    // foi pago — e o aviso laranja de movimentação
                                    // reforçava a impressão de que era esse o caminho.
                                    setApproveMode(
                                        item.externalTransferVia
                                            ? 'ALREADY_TRANSFERRED'
                                            : 'TRANSFER',
                                    )
                                    setTransferredVia(
                                        item.externalTransferVia ?? '',
                                    )
                                    setConfirming({
                                        kind: 'payable',
                                        ids: [item.id],
                                        action: 'approve',
                                    })
                                }}
                                onReject={() =>
                                    setConfirming({
                                        kind: 'payable',
                                        ids: [item.id],
                                        action: 'reject',
                                    })
                                }
                                onMarkExternal={() => setExternalTarget(item)}
                                onClearExternal={() =>
                                    handleClearExternal(item.id)
                                }
                                marking={markingExternal === item.id}
                                onViewGroup={
                                    item.isPayoutBatch
                                        ? () => setBatchTarget(item)
                                        : undefined
                                }
                                onOpenStatement={(() => {
                                    // Reaproveita o visualizador de comprovante: ele
                                    // já renderiza PDF em iframe e tem o botão de
                                    // baixar, então "ver" e "baixar" saem do mesmo
                                    // clique.
                                    const statement = findStatementAttachment(
                                        item.attachments
                                    )
                                    return statement
                                        ? () => setPreviewReceipt(statement)
                                        : undefined
                                })()}
                            />
                            </div>
                        )}
                    />
                )}

                {tab === 'actions' && (
                    <QueueSection
                        items={actionRequests}
                        loading={loading}
                        page={actionsPage}
                        onPageChange={setActionsPage}
                        label="ação"
                        emptyTitle={
                            search
                                ? 'Nenhuma ação encontrada para esta busca.'
                                : 'Nenhuma ação aguardando aprovação.'
                        }
                        emptyHint={
                            search
                                ? 'A busca cobre cliente, parceiro, descrição e o número do lançamento.'
                                : 'Pedidos de baixa manual e de cancelamento feitos por quem não é operador financeiro aparecem aqui.'
                        }
                        selection={
                            canApproveActions
                                ? {
                                      selectedCount: selectedActions.length,
                                      totalSelectable: actionRequests.length,
                                      onToggleAll: () =>
                                          toggleAll(
                                              actionRequests.map(r => r.id),
                                              selectedActions,
                                              setSelectedActions
                                          ),
                                      onApprove: () =>
                                          setConfirming({
                                              kind: 'action',
                                              ids: selectedActions,
                                              action: 'approve',
                                          }),
                                      onReject: () =>
                                          setConfirming({
                                              kind: 'action',
                                              ids: selectedActions,
                                              action: 'reject',
                                          }),
                                      onClear: () => setSelectedActions([]),
                                  }
                                : undefined
                        }
                        renderItem={item => (
                            <div {...focusFrame('actions', item.id)}>
                            <ActionRequestCard
                                item={item}
                                selectable={canApproveActions}
                                selected={selectedActions.includes(item.id)}
                                onToggleSelect={() =>
                                    toggleOne(
                                        item.id,
                                        selectedActions,
                                        setSelectedActions
                                    )
                                }
                                canApprove={canApproveActions}
                                canDelete={canDeleteQueueItem}
                                onDelete={() => setDeleting(item)}
                                onApprove={() =>
                                    setConfirming({
                                        kind: 'action',
                                        ids: [item.id],
                                        action: 'approve',
                                    })
                                }
                                onReject={() =>
                                    setConfirming({
                                        kind: 'action',
                                        ids: [item.id],
                                        action: 'reject',
                                    })
                                }
                                onOpenReceipt={
                                    item.billing.receipt
                                        ? () =>
                                              setPreviewReceipt(
                                                  item.billing.receipt!
                                              )
                                        : undefined
                                }
                            />
                            </div>
                        )}
                    />
                )}

                {tab === 'refunds' && (
                    <QueueSection
                        items={refunds}
                        loading={loading}
                        page={refundsPage}
                        onPageChange={setRefundsPage}
                        label="solicitação"
                        emptyTitle={
                            search
                                ? 'Nenhum estorno encontrado para esta busca.'
                                : 'Nenhum estorno aguardando aprovação.'
                        }
                        emptyHint={
                            search
                                ? 'A busca cobre cliente, parceiro, descrição e o número da cobrança.'
                                : 'Cancelar um atendimento já pago abre uma solicitação de estorno aqui.'
                        }
                        selection={
                            canApprovePayables
                                ? {
                                      selectedCount: selectedRefunds.length,
                                      totalSelectable: refunds.length,
                                      amountText: `Total ${formatCurrency(sumRefunds(selectedRefunds))}`,
                                      onToggleAll: () =>
                                          toggleAll(
                                              refunds.map(r => r.id),
                                              selectedRefunds,
                                              setSelectedRefunds
                                          ),
                                      onApprove: () =>
                                          setConfirming({
                                              kind: 'refund',
                                              ids: selectedRefunds,
                                              action: 'approve',
                                          }),
                                      onReject: () =>
                                          setConfirming({
                                              kind: 'refund',
                                              ids: selectedRefunds,
                                              action: 'reject',
                                          }),
                                      onClear: () => setSelectedRefunds([]),
                                  }
                                : undefined
                        }
                        renderItem={item => (
                            <div {...focusFrame('refunds', item.id)}>
                            <RefundCard
                                item={item}
                                selectable={canApprovePayables}
                                selected={selectedRefunds.includes(item.id)}
                                onToggleSelect={() =>
                                    toggleOne(
                                        item.id,
                                        selectedRefunds,
                                        setSelectedRefunds
                                    )
                                }
                                canApprove={canApproveRefund(item)}
                                onApprove={() =>
                                    setConfirming({
                                        kind: 'refund',
                                        ids: [item.id],
                                        action: 'approve',
                                    })
                                }
                                onReject={() =>
                                    setConfirming({
                                        kind: 'refund',
                                        ids: [item.id],
                                        action: 'reject',
                                    })
                                }
                                onMarkExternal={() =>
                                    handleAlreadyRefunded(item)
                                }
                                onClearExternal={() =>
                                    handleClearRefundExternal(item.id)
                                }
                                onDelete={
                                    canDeleteQueueItem
                                        ? () => setDeletingRefund(item)
                                        : undefined
                                }
                                marking={markingRefundExternal === item.id}
                            />
                            </div>
                        )}
                    />
                )}

                {tab === 'approved' && <ApprovedHistorySection search={search} />}
            </div>

            {/* Aprovação de ESTORNO — exige senha (devolve dinheiro ao cliente) */}
            {confirming?.action === 'approve' &&
                confirming.kind === 'refund' && (
                    <dialog open className="modal modal-open">
                        <div className="modal-box max-w-md">
                            <h3 className="font-bold text-lg mb-2">
                                {confirmingCount > 1
                                    ? `Aprovar ${confirmingCount} estornos`
                                    : 'Aprovar estorno'}
                            </h3>

                            {refundMode === 'BAAS' && (
                                <div className="alert alert-warning items-start gap-3 mb-4">
                                    <MdWarning className="text-2xl shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <p className="font-semibold">
                                            O dinheiro será devolvido ao cliente
                                        </p>
                                        <p className="mt-1">
                                            Valor:{' '}
                                            <span className="font-bold text-error">
                                                {formatCurrency(
                                                    sumRefunds(confirming.ids)
                                                )}
                                            </span>
                                            {confirmingCount > 1 &&
                                                ` (${confirmingCount} cobranças)`}
                                        </p>
                                        <p className="mt-2">
                                            O estorno é executado no provedor e
                                            não pode ser desfeito.
                                        </p>
                                        {refunds
                                            .filter(r =>
                                                confirming.ids.includes(r.id)
                                            )
                                            .some(
                                                r =>
                                                    r.billing
                                                        .isProviderInstallment
                                            ) && (
                                            /* Cartão parcelado: o estorno é feito no
                                               PARCELAMENTO, e é o provedor que decide de
                                               quais parcelas o valor sai. */
                                            <p className="mt-2">
                                                {confirmingCount > 1
                                                    ? 'Há cobranças pagas em cartão parcelado: nelas o estorno é feito no parcelamento, e o provedor decide de quais parcelas o valor sai.'
                                                    : 'Pagamento em cartão parcelado: o estorno é feito no parcelamento, e o provedor decide de quais parcelas o valor sai.'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Cobrança paga por fora não tem o que estornar no provedor:
                                o modo automático nem é oferecido, para o operador não
                                aprovar algo que o backend recusaria. */}
                            {!refundSelectionCanUseBaas(confirming.ids) && (
                                <div className="alert alert-info items-start gap-3 mb-4">
                                    <MdInfoOutline className="text-xl shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        {confirmingCount > 1
                                            ? 'Ao menos uma das cobranças não foi paga pela conta digital.'
                                            : 'Esta cobrança não foi paga pela conta digital.'}{' '}
                                        A devolução precisa ser feita por fora e
                                        registrada aqui.
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2 mb-4">
                                {/* `BAAS` sai da lista quando a cobrança não entrou pelo
                                    provedor (não há o que estornar lá) **e** quando quem
                                    está aprovando não é operador financeiro eleito: aquele
                                    modo movimenta saldo da conta digital, e é o único que
                                    continua restrito. Quem tem só `delete` fica com "já
                                    foi estornado", que é lançamento. */}
                                {REFUND_MODES.filter(
                                    option =>
                                        option.value !== 'BAAS' ||
                                        (isOperator &&
                                            refundSelectionCanUseBaas(
                                                confirming.ids
                                            ))
                                ).map(option => (
                                    <label
                                        key={option.value}
                                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                            refundMode === option.value
                                                ? 'border-secondary bg-secondary/5'
                                                : 'border-base-300 hover:bg-base-200/40'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="refund-mode"
                                            className="radio radio-secondary radio-sm mt-0.5"
                                            checked={refundMode === option.value}
                                            onChange={() =>
                                                setRefundMode(option.value)
                                            }
                                            disabled={processing}
                                        />
                                        <span>
                                            <span className="font-medium block">
                                                {option.label}
                                            </span>
                                            <span className="text-xs text-base-content/60">
                                                {option.description}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {refundMode === 'ALREADY_REFUNDED' && (
                                <div className="form-control mb-4">
                                    <label className="label py-1">
                                        <span className="label-text">
                                            Instituição por onde o estorno foi
                                            feito{' '}
                                            <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: Banco do Brasil, Itaú, dinheiro em espécie"
                                        value={refundedVia}
                                        onChange={e =>
                                            setRefundedVia(e.target.value)
                                        }
                                        disabled={processing}
                                    />
                                    <label className="label py-1">
                                        <span className="label-text-alt text-base-content/50">
                                            A cobrança será marcada como
                                            estornada, sem movimentar a conta
                                            digital.
                                        </span>
                                    </label>
                                </div>
                            )}

                            <PasswordField
                                value={password}
                                onChange={setPassword}
                                disabled={processing}
                            />
                            {processing && confirmingCount > 1 && (
                                <p className="text-xs text-base-content/60 mt-3">
                                    Processando {confirmingCount} estornos. Não
                                    feche esta janela.
                                </p>
                            )}
                            <div className="modal-action">
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => {
                                        setConfirming(null)
                                        setPassword('')
                                        setRefundMode('BAAS')
                                        setRefundedVia('')
                                    }}
                                    disabled={processing}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleConfirm}
                                    disabled={
                                        processing ||
                                        !password ||
                                        (refundMode === 'ALREADY_REFUNDED' &&
                                            !refundedVia.trim())
                                    }
                                >
                                    {processing ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        `${REFUND_MODE_ACTION[refundMode]}${confirmingCount > 1 ? ` (${confirmingCount})` : ''}`
                                    )}
                                </button>
                            </div>
                        </div>
                    </dialog>
                )}

            {/* Aprovação de action-request (mark-paid/cancel) — sem senha */}
            <ConfirmModal
                isOpen={
                    confirming?.action === 'approve' &&
                    confirming.kind === 'action'
                }
                onClose={() => setConfirming(null)}
                onConfirm={handleConfirm}
                title={
                    (confirming?.ids.length ?? 0) > 1
                        ? `Aprovar ${confirming?.ids.length} ações`
                        : 'Aprovar ação'
                }
                message={
                    (confirming?.ids.length ?? 0) > 1
                        ? `${confirming?.ids.length} ações serão executadas imediatamente nos lançamentos. Confirmar?`
                        : 'A ação será executada imediatamente no lançamento. Confirmar?'
                }
                loading={processing}
                confirmButtonText="Aprovar"
                cancelButtonText="Cancelar"
            />

            {/* Exclusão da solicitação — não decide a ação, só tira da fila */}
            <ConfirmModal
                isOpen={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={handleDelete}
                title="Excluir solicitação"
                message={
                    deleting
                        ? `A solicitação para o lançamento #${deleting.billingId} sai da fila sem ser aprovada nem recusada, e o lançamento volta a aceitar um novo pedido. Nada é executado na cobrança. Excluir?`
                        : ''
                }
                loading={deletingBusy}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />

            {/* Exclusão da conta a pagar — o pedido volta a não existir. No lote, avisa
                o que acontece com os repasses, que é o que o financeiro precisa saber
                para refazer o recorte. */}
            <ConfirmModal
                isOpen={!!deletingPayable}
                onClose={() => setDeletingPayable(null)}
                onConfirm={handleDeletePayable}
                title="Excluir solicitação"
                message={
                    deletingPayable
                        ? deletingPayable.isPayoutBatch
                            ? `A conta agrupada #${deletingPayable.id} de ${formatCurrency(deletingPayable.amount)} será apagada e os repasses que ela soma voltam para o agrupamento, prontos para uma nova solicitação. Nada é aprovado nem recusado, e nenhum dinheiro é movimentado. Excluir?`
                            : `A conta a pagar #${deletingPayable.id} de ${formatCurrency(deletingPayable.amount)} sai da fila sem ser aprovada nem recusada. A conta continua existindo e pode ser enviada de novo. Excluir da fila?`
                        : ''
                }
                loading={deletingBusy}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />

            {/* Exclusão do pedido de ESTORNO — nada é decidido, a cobrança volta ao
                estado normal e aceita um pedido novo. */}
            <ConfirmModal
                isOpen={!!deletingRefund}
                onClose={() => setDeletingRefund(null)}
                onConfirm={handleDeleteRefund}
                title="Excluir solicitação de estorno"
                message={
                    deletingRefund
                        ? `O pedido de estorno de ${formatCurrency(deletingRefund.payload?.refundAmount ?? 0)} da cobrança #${deletingRefund.billingId} sai da fila sem ser aprovado nem recusado. Nenhum dinheiro é devolvido, e a cobrança volta a aceitar um novo pedido. Excluir?`
                        : ''
                }
                loading={deletingBusy}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />

            {/* Aprovação de PAYABLE — exige senha (dispara transferência no provedor) */}
            {confirming?.action === 'approve' &&
                confirming.kind === 'payable' && (
                    <dialog open className="modal modal-open">
                        <div className="modal-box max-w-md">
                            <h3 className="font-bold text-lg mb-2">
                                {confirmingCount > 1
                                    ? `Aprovar ${confirmingCount} contas a pagar`
                                    : 'Aprovar conta a pagar'}
                            </h3>
                            {/* Dinheiro saindo da conta: o aviso precisa ser explícito
                                e o valor, visível antes da senha. Só aparece quando a
                                aprovação de fato movimenta a conta digital. */}
                            {approveMode === 'TRANSFER' && (
                                <div className="alert alert-warning items-start gap-3 mb-4">
                                    <MdWarning className="text-2xl shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <p className="font-semibold">
                                            Vai ocorrer uma movimentação de
                                            dinheiro na conta digital
                                        </p>
                                        <p className="mt-1">
                                            Valor:{' '}
                                            <span className="font-bold text-error">
                                                {formatCurrency(
                                                    sumPayables(confirming.ids)
                                                )}
                                            </span>
                                            {confirmingCount > 1 &&
                                                ` (${confirmingCount} contas)`}
                                        </p>
                                        <p className="mt-2">
                                            Verifique se este pagamento já não
                                            foi feito por outro meio ou por
                                            outro banco — a transferência é
                                            executada imediatamente e não pode
                                            ser desfeita.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Como a conta será quitada. É a escolha do aprovador:
                                nem toda conta é paga pela conta digital. */}
                            <div className="space-y-2 mb-4">
                                {APPROVE_MODES.map(option => (
                                    <label
                                        key={option.value}
                                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                            approveMode === option.value
                                                ? 'border-secondary bg-secondary/5'
                                                : 'border-base-300 hover:bg-base-200/40'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="approve-mode"
                                            className="radio radio-secondary radio-sm mt-0.5"
                                            checked={
                                                approveMode === option.value
                                            }
                                            onChange={() =>
                                                setApproveMode(option.value)
                                            }
                                            disabled={processing}
                                        />
                                        <span>
                                            <span className="font-medium block">
                                                {option.label}
                                            </span>
                                            <span className="text-xs text-base-content/60">
                                                {option.description}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {approveMode === 'ALREADY_TRANSFERRED' && (
                                <div className="form-control mb-4">
                                    <label className="label py-1">
                                        <span className="label-text">
                                            Instituição onde a transferência foi
                                            feita{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    {/* `autoComplete="off"`: o Chrome tratava este
                                        campo como e-mail e preenchia sozinho com o
                                        endereço do usuário — e o valor vira o registro
                                        de onde o dinheiro saiu, no histórico e na
                                        conciliação. Visto no teste de 2026-08-02. */}
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: Banco do Brasil, Itaú, Nubank"
                                        value={transferredVia}
                                        onChange={e =>
                                            setTransferredVia(e.target.value)
                                        }
                                        disabled={processing}
                                        autoComplete="off"
                                        name="instituicao-transferencia"
                                    />
                                    {/* Sem texto de apoio aqui: a descrição do próprio
                                        modo, logo acima, já diz que a conta é marcada
                                        como paga e conciliada sem nova transferência.
                                        Repetido, empurrava o modal para além da tela e
                                        criava rolagem justo na hora de digitar a
                                        senha. */}
                                </div>
                            )}

                            <PasswordField
                                value={password}
                                onChange={setPassword}
                                disabled={processing}
                            />
                            {processing && confirmingCount > 1 && (
                                <p className="text-xs text-base-content/60 mt-3">
                                    Processando {confirmingCount} contas. Não
                                    feche esta janela.
                                </p>
                            )}
                            <div className="modal-action">
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => {
                                        setConfirming(null)
                                        setPassword('')
                                        setApproveMode('TRANSFER')
                                        setTransferredVia('')
                                    }}
                                    disabled={processing}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleConfirm}
                                    disabled={
                                        processing ||
                                        !password ||
                                        (approveMode ===
                                            'ALREADY_TRANSFERRED' &&
                                            !transferredVia.trim())
                                    }
                                >
                                    {processing ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        `${APPROVE_MODE_ACTION[approveMode]}${confirmingCount > 1 ? ` (${confirmingCount})` : ''}`
                                    )}
                                </button>
                            </div>
                        </div>
                    </dialog>
                )}

            {confirming?.action === 'reject' && (
                <dialog open className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">
                            {confirming.kind === 'action'
                                ? confirmingCount > 1
                                    ? `Rejeitar ${confirmingCount} ações`
                                    : 'Rejeitar ação'
                                : confirming.kind === 'refund'
                                  ? confirmingCount > 1
                                      ? `Recusar ${confirmingCount} estornos`
                                      : 'Recusar estorno'
                                  : confirmingCount > 1
                                    ? `Rejeitar ${confirmingCount} contas a pagar`
                                    : 'Rejeitar conta a pagar'}
                        </h3>
                        {confirming.kind === 'refund' && (
                            <p className="text-sm text-base-content/70 mb-4">
                                O dinheiro <strong>não</strong> será devolvido ao
                                cliente. O atendimento continua cancelado — só o
                                estorno é recusado.
                            </p>
                        )}
                        {confirming.kind === 'action' && (
                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">
                                        Categoria do motivo{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-start gap-3 p-3 rounded-xl border border-base-300 cursor-pointer hover:bg-base-200/40">
                                        <input
                                            type="radio"
                                            name="reason-category"
                                            className="radio radio-error mt-0.5"
                                            checked={
                                                reasonCategory === 'RECEIPT'
                                            }
                                            onChange={() =>
                                                setReasonCategory('RECEIPT')
                                            }
                                        />
                                        <span>
                                            <span className="font-medium block">
                                                Comprovante
                                            </span>
                                            <span className="text-xs text-base-content/60">
                                                O problema está no documento
                                                (falso, ilegível, valor
                                                divergente). Ele fica invalidado
                                                e não poderá ser reenviado nesta
                                                cobrança.
                                            </span>
                                        </span>
                                    </label>
                                    <label className="flex items-start gap-3 p-3 rounded-xl border border-base-300 cursor-pointer hover:bg-base-200/40">
                                        <input
                                            type="radio"
                                            name="reason-category"
                                            className="radio mt-0.5"
                                            checked={reasonCategory === 'OTHER'}
                                            onChange={() =>
                                                setReasonCategory('OTHER')
                                            }
                                        />
                                        <span>
                                            <span className="font-medium block">
                                                Outro
                                            </span>
                                            <span className="text-xs text-base-content/60">
                                                Recusa apenas esta solicitação.
                                                O comprovante continua válido e
                                                pode ser usado numa nova
                                                solicitação.
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">
                                    Motivo da rejeição
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered"
                                rows={3}
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                            />
                            {confirmingCount > 1 && (
                                <span className="text-xs text-base-content/60 mt-2">
                                    O mesmo motivo será registrado nas{' '}
                                    {confirmingCount} rejeições.
                                </span>
                            )}
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setConfirming(null)
                                    setReason('')
                                    setReasonCategory(null)
                                }}
                                disabled={processing}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleConfirm}
                                disabled={
                                    processing ||
                                    !reason.trim() ||
                                    (confirming.kind === 'action' &&
                                        !reasonCategory)
                                }
                            >
                                {processing ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    'Rejeitar'
                                )}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            {/* Já foi transferida por fora — pede a instituição. Disponível a quem
                enxerga a fila: é declaração de um pagamento já ocorrido em outro
                banco, não uma movimentação daqui. */}
            {externalTarget && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-md">
                        <h3 className="font-bold text-lg mb-2">
                            Já foi transferida
                        </h3>
                        <p className="text-sm text-base-content/70 mb-4">
                            Registra que esta conta de{' '}
                            <span className="font-semibold text-error">
                                {formatCurrency(externalTarget.amount)}
                            </span>{' '}
                            já foi paga por fora da conta digital. Ela sai da
                            fila dada como paga e conciliada — nenhuma
                            transferência é feita por aqui.
                        </p>

                        <div className="form-control">
                            <label className="label py-1">
                                <span className="label-text">
                                    Instituição onde a transferência foi feita{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Banco do Brasil, Itaú, Nubank"
                                value={externalVia}
                                onChange={e => setExternalVia(e.target.value)}
                                disabled={markingExternal === externalTarget.id}
                            />
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setExternalTarget(null)
                                    setExternalVia('')
                                }}
                                disabled={markingExternal === externalTarget.id}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-info"
                                onClick={handleMarkExternal}
                                disabled={
                                    !externalVia.trim() ||
                                    markingExternal === externalTarget.id
                                }
                            >
                                {markingExternal === externalTarget.id ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    'Confirmar'
                                )}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            {refundExternalTarget && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-md">
                        <h3 className="font-bold text-lg mb-2">
                            Marcar como já estornado
                        </h3>
                        <p className="text-sm text-base-content/70 mb-4">
                            Registra que este estorno de{' '}
                            <span className="font-semibold text-error">
                                {formatCurrency(
                                    refundExternalTarget.payload
                                        ?.refundAmount ??
                                        refundExternalTarget.billing
                                            .refundableAmount
                                )}
                            </span>{' '}
                            já foi devolvido ao cliente por fora (outro banco).
                            Ao aprovar, nenhum estorno será feito de novo — a
                            devolução é dada como concluída.
                        </p>

                        <div className="form-control">
                            <label className="label py-1">
                                <span className="label-text">
                                    Instituição por onde o estorno foi feito{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Banco do Brasil, Itaú, Nubank"
                                value={refundExternalVia}
                                onChange={e =>
                                    setRefundExternalVia(e.target.value)
                                }
                                disabled={
                                    markingRefundExternal ===
                                    refundExternalTarget.id
                                }
                            />
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setRefundExternalTarget(null)
                                    setRefundExternalVia('')
                                }}
                                disabled={
                                    markingRefundExternal ===
                                    refundExternalTarget.id
                                }
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-info"
                                onClick={handleMarkRefundExternal}
                                disabled={
                                    !refundExternalVia.trim() ||
                                    markingRefundExternal ===
                                        refundExternalTarget.id
                                }
                            >
                                {markingRefundExternal ===
                                refundExternalTarget.id ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    'Marcar como estornado'
                                )}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            {batchTarget && (
                <PayoutBatchItemsModal
                    batchId={batchTarget.id}
                    recipientName={
                        batchTarget.supplier?.name || batchTarget.partner?.name
                    }
                    onClose={() => setBatchTarget(null)}
                />
            )}

            {previewReceipt && (
                <ReceiptPreviewModal
                    isOpen
                    onClose={() => setPreviewReceipt(null)}
                    url={previewReceipt.url}
                    name={previewReceipt.name}
                />
            )}
        </div>
    )
}
